import { useCallback, useContext, useState } from "react";
import { GroupSettingsContext } from "../contexts/GroupSettingsContext";
import { TableContext } from "../contexts/TableContext";
import { DashboardsTableContext } from "../contexts/TablesContext/DashboardsTableProvider";

const useDashboardsTableHeader = () => {
  const { tagGroups, setTagGroups, customFields, setCustomFields } = useContext(GroupSettingsContext);
  const { setVisibleHeaders } = useContext(TableContext);
  const { headers, filters, columns, sortBy, setHeaders, setSortBy } = useContext(DashboardsTableContext);

  const [manageModalOpen, setManageModalOpen] = useState<boolean>(false);
  const [manageStatusModalOpen, setManageStatusModalOpen] = useState<boolean>(false);
  const [manageTagsModalOpen, setManageTagsModalOpen] = useState<boolean>(false);
  const [manageStagesModalOpen, setManageStagesModalOpen] = useState<boolean>(false);
  const [manageKeyPeopleTagsModalOpen, setManageKeyPeopleTagsModalOpen] = useState<boolean>(false);
  const [manageCustomFieldsModalOpen, setManageCustomFieldsModalOpen] = useState<boolean>(false);
  const [manageFieldsModalOpen, setManageFieldsModalOpen] = useState<boolean>(false);

  const showColumn = useCallback((column: string) => columns?.includes(column), [columns]);

  const handleColumnSort = useCallback((column: string) => {
    setSortBy(column);
  }, [setSortBy]);

  const handleMoveLeft = useCallback((field?: string) => {
    if (!!field) {
      setHeaders(prev => {
        const index = (prev ?? []).indexOf(field);

        if (index > 1) {
          const newHeaders = [...(prev ?? [])];
          newHeaders.splice(index, 1);
          newHeaders.splice(index - 1, 0, field);
          return newHeaders;
        }

        return prev;
      });
    }
  }, [setHeaders]);

  const handleMoveRight = useCallback((field?: string) => {
    if (!!field) {
      setHeaders(prev => {
        const index = (prev ?? []).indexOf(field);

        if (index < (prev ?? []).length - 1) {
          const newHeaders = [...(prev ?? [])];
          newHeaders.splice(index, 1);
          newHeaders.splice(index + 1, 0, field);
          return newHeaders;
        }

        return prev;
      });
    }
  }, [setHeaders]);

  const handleEdit = useCallback((field?: string) => {
    if (!!field && !field?.match(/\[\[new-(.+?)\]\]/)) {
      if (field === 'Status')
        setManageStatusModalOpen(true);
      else if (field === 'Stage')
        setManageStagesModalOpen(true);
      else if (tagGroups.find(({title}) => title === field))
        setManageTagsModalOpen(true);
      else if (customFields.find(({name}) => name === field))
        setManageCustomFieldsModalOpen(true);
      else if (field === 'Key People')
        setManageKeyPeopleTagsModalOpen(true);
    } else if (field?.match(/\[\[new-(.+?)\]\]/)) {
      const matchedField = field.match(/\[\[new-(.+?)\]\]/)?.[1];
      if (matchedField === 'tag')
        setManageTagsModalOpen(true);
      else if (matchedField === 'field')
        setManageCustomFieldsModalOpen(true);
    }
    setManageModalOpen(false);
  }, [tagGroups, customFields]);

  const handleDelete = useCallback((field?: string) => {
    if (tagGroups.find(({title}) => title === field)){
      setTagGroups(prev => prev.filter(tagGroup => tagGroup.title.toLowerCase() !== field!.toLowerCase())
        .map(({ title, tags, tagsPerDashboard, additionalContextQuery }) => ({ title, tags, tagsPerDashboard, additionalContextQuery })));
    } else if (customFields.find(({name}) => name === field)) {
      setCustomFields(prev => prev.filter(custom => custom.name.toLowerCase() !== field!.toLowerCase())
        .map(({ name, additionalContextQuery }) => ({ name, additionalContextQuery })));
    }
    setManageModalOpen(false);
  // eslint-disable-next-line
  }, [tagGroups, customFields]);

  const handleHideColumn = useCallback((field?: string) => {
    setVisibleHeaders(prev => [...prev ?? []].map((state, i) => headers?.indexOf(field || '') === i ? !state : state));
  }, [headers, setVisibleHeaders]);

  const handleManage = useCallback(() => {
    setManageFieldsModalOpen(true);
  }, []);

  return {
    headers,
    filters,
    columns,
    sortBy,
    manageModalOpen,
    manageStatusModalOpen,
    manageTagsModalOpen,
    manageStagesModalOpen,
    manageKeyPeopleTagsModalOpen,
    manageCustomFieldsModalOpen,
    manageFieldsModalOpen,
    setManageModalOpen,
    setManageStatusModalOpen,
    setManageTagsModalOpen,
    setManageStagesModalOpen,
    setManageKeyPeopleTagsModalOpen,
    setManageCustomFieldsModalOpen,
    setManageFieldsModalOpen,
    showColumn,
    handleColumnSort,
    handleMoveLeft,
    handleMoveRight,
    handleEdit,
    handleDelete,
    handleHideColumn,
    handleManage,
  };
};

export default useDashboardsTableHeader;